/**
 * Zone labels as real DOM text over the canvas: sargam (Yatra One) with the letter name beneath (Inter).
 * Bone text on a locally darkened disc plus a text-shadow, so contrast never depends on the shader.
 * Purely decorative for assistive tech (aria-hidden, pointer-events none); the play screen provides the
 * accessible per-zone buttons. Labels are recomputed only when the layout, scale or tonic changes.
 */
import { memo, useMemo } from 'react';
import type { Scale } from '@tonist/shared';
import type { MandalaLayout } from './geometry';
import { computeLabels, type Label } from './labels';

export interface MandalaLabelsProps {
  layout: MandalaLayout;
  scale: Scale;
  tonicPc: number;
  /** keys of zones currently sounding (zoneKey), brightened */
  activeKeys?: ReadonlySet<string>;
  /** fade labels under the scale-change wave, 0..1 (optional, default 1) */
  opacity?: number;
}

const BONE = '#F3EEE3';
const GOLD_HI = '#FFE08A';
const SARGAM_FONT = "'Yatra One', 'Noto Serif Devanagari', serif";
const LETTER_FONT = "Inter, system-ui, sans-serif";

/** Font sizes in css px derived from the mandala radius so labels scale with the viewport. */
export function labelSizes(R: number): { sargam: number; letter: number; fire: number; disc: number } {
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  return {
    sargam: clamp(R * 0.052, 12, 22),
    letter: clamp(R * 0.034, 10, 14),
    fire: clamp(R * 0.042, 12, 18),
    disc: clamp(R * 0.11, 30, 52),
  };
}

function styleFor(l: Label, sizes: ReturnType<typeof labelSizes>, active: boolean, opacity: number): React.CSSProperties {
  const isFire = l.ring === 4;
  const dim = isFire && !l.inScale;
  const disc = isFire ? sizes.disc * 0.8 : l.ring === 3 ? sizes.disc * 0.7 : sizes.disc;
  return {
    position: 'absolute',
    left: l.x, top: l.y,
    transform: 'translate(-50%, -50%)',
    width: disc, height: disc,
    borderRadius: '50%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    lineHeight: 1.05,
    color: l.isTonic ? GOLD_HI : BONE,
    opacity: (dim ? 0.42 : 1) * opacity,
    background: 'radial-gradient(circle, rgba(8,7,12,0.62) 0%, rgba(8,7,12,0.45) 55%, rgba(8,7,12,0) 100%)',
    textShadow: '0 0 4px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)',
    filter: active ? 'brightness(1.25)' : undefined,
    transition: 'opacity 300ms ease, filter 200ms ease',
    whiteSpace: 'nowrap',
    fontWeight: l.isTonic || l.gate >= 0 ? 700 : 500,
  };
}

function MandalaLabelsImpl({ layout, scale, tonicPc, activeKeys, opacity = 1 }: MandalaLabelsProps) {
  const labels = useMemo(() => computeLabels(layout, scale, tonicPc), [layout, scale, tonicPc]);
  const sizes = useMemo(() => labelSizes(layout.R), [layout.R]);
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1 }}>
      {labels.map((l) => {
        const active = activeKeys?.has(l.key) ?? false;
        const isFire = l.ring === 4;
        const primaryIsSargam = l.ring === 0 || l.ring === 1 || l.ring === 2;
        return (
          <div key={l.key} data-zone={l.key} style={styleFor(l, sizes, active, opacity)}>
            <span style={{
              fontFamily: primaryIsSargam ? SARGAM_FONT : LETTER_FONT,
              fontSize: isFire ? sizes.fire : primaryIsSargam ? sizes.sargam : sizes.letter,
              letterSpacing: primaryIsSargam ? undefined : '0.02em',
            }}>{l.primary}</span>
            {l.secondary ? (
              <span style={{ fontFamily: LETTER_FONT, fontSize: sizes.letter, opacity: 0.85, fontWeight: 400 }}>{l.secondary}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export const MandalaLabels = memo(MandalaLabelsImpl);
